import { supabase } from '@/integrations/supabase/client';

/**
 * Ready-made food safety checks every kitchen needs on day one.
 * These get created automatically so a brand new account is never empty.
 */
export const STARTER_TEMPLATES = [
  {
    name: 'Daily Fridge & Freezer Temperatures',
    description: 'Log every cold unit at the start of service. Required evidence for any inspection.',
    frequency: 'daily',
    items: [
      {
        id: 'fridge_1',
        type: 'temperature',
        label: 'Fridge 1 temperature',
        description: 'Must read between 1°C and 4°C',
        required: true,
        min_value: 1,
        max_value: 4,
        unit: '°C',
        category: 'cold_holding',
      },
      {
        id: 'fridge_2',
        type: 'temperature',
        label: 'Fridge 2 / prep fridge temperature',
        description: 'Must read between 1°C and 4°C',
        required: false,
        min_value: 1,
        max_value: 4,
        unit: '°C',
        category: 'cold_holding',
      },
      {
        id: 'freezer_1',
        type: 'temperature',
        label: 'Freezer temperature',
        description: 'Must read -18°C or colder',
        required: true,
        min_value: -30,
        max_value: -18,
        unit: '°C',
        category: 'cold_holding',
      },
      {
        id: 'cold_corrective',
        type: 'text',
        label: 'Action taken if any unit was out of range',
        description: 'Moved stock, called engineer, discarded product, etc.',
        required: false,
        category: 'cold_holding',
      },
    ],
  },
  {
    name: 'Opening Food Safety Checks',
    description: 'Two-minute walk round before service starts.',
    frequency: 'daily',
    items: [
      {
        id: 'handwash',
        type: 'checkbox',
        label: 'Hand wash basins stocked with soap and paper towels',
        required: true,
        category: 'hygiene',
      },
      {
        id: 'staff_fit',
        type: 'checkbox',
        label: 'All staff fit to work, clean uniform, no open cuts uncovered',
        required: true,
        category: 'hygiene',
      },
      {
        id: 'pest_check',
        type: 'checkbox',
        label: 'No signs of pests (droppings, gnaw marks, flies)',
        required: true,
        category: 'pest_control',
      },
      {
        id: 'sanitiser',
        type: 'checkbox',
        label: 'Sanitiser made up to correct strength and in date',
        required: true,
        category: 'cleaning',
      },
      {
        id: 'probe_check',
        type: 'checkbox',
        label: 'Probe thermometer working and wiped with probe wipes',
        required: true,
        category: 'equipment',
      },
    ],
  },
  {
    name: 'Closing Kitchen Hygiene & Storage',
    description: 'End of shift close-down and food storage check.',
    frequency: 'daily',
    items: [
      {
        id: 'covered_dated',
        type: 'checkbox',
        label: 'All open food covered, labelled and date marked',
        required: true,
        category: 'storage',
      },
      {
        id: 'raw_ready',
        type: 'checkbox',
        label: 'Raw and ready-to-eat food stored separately',
        required: true,
        category: 'storage',
      },
      {
        id: 'surfaces',
        type: 'checkbox',
        label: 'Surfaces, equipment and floors cleaned and sanitised',
        required: true,
        category: 'cleaning',
      },
      {
        id: 'waste',
        type: 'checkbox',
        label: 'Waste removed and bin area clean, lids closed',
        required: true,
        category: 'cleaning',
      },
      {
        id: 'closing_notes',
        type: 'text',
        label: 'Anything to hand over to the next shift?',
        required: false,
        category: 'general',
      },
    ],
  },
];

/**
 * Creates the starter templates for a company (if they don't already exist)
 * and schedules today's first checklist against the given location.
 * Returns the id of the first checklist created, if any.
 */
export async function provisionStarterChecklists(
  tenantId: string,
  unitId: string,
  userId?: string
): Promise<string | null> {
  if (!tenantId || !unitId) return null;

  const names = STARTER_TEMPLATES.map((t) => t.name);

  const { data: existing } = await supabase
    .from('checklist_templates')
    .select('id, name')
    .eq('tenant_id', tenantId)
    .in('name', names);

  const existingByName = new Map((existing || []).map((t: any) => [t.name, t.id as string]));

  const toCreate = STARTER_TEMPLATES.filter((t) => !existingByName.has(t.name));

  if (toCreate.length > 0) {
    const { data: inserted, error } = await supabase
      .from('checklist_templates')
      .insert(
        toCreate.map((t) => ({
          tenant_id: tenantId,
          name: t.name,
          description: t.description,
          frequency: t.frequency,
          items: t.items as any,
          is_active: true,
          template_source: 'starter_pack',
          template_category: 'starter',
        }))
      )
      .select('id, name');

    if (error) throw error;
    (inserted || []).forEach((t: any) => existingByName.set(t.name, t.id));
  }

  const templateIds = names
    .map((n) => existingByName.get(n))
    .filter((id): id is string => Boolean(id));

  if (templateIds.length === 0) return null;

  // Don't double-schedule if checklists already exist for this location today.
  const startOfDay = new Date();
  startOfDay.setHours(0, 0, 0, 0);

  const { count } = await supabase
    .from('checklists')
    .select('*', { count: 'exact', head: true })
    .eq('unit_id', unitId)
    .gte('scheduled_for', startOfDay.toISOString());

  if ((count || 0) > 0) return null;

  const { data: checklists, error: checklistError } = await supabase
    .from('checklists')
    .insert(
      templateIds.map((templateId) => ({
        unit_id: unitId,
        template_id: templateId,
        scheduled_for: new Date().toISOString(),
        status: 'pending' as const,
        assigned_to: userId || null,
      }))
    )
    .select('id');

  if (checklistError) throw checklistError;

  return checklists?.[0]?.id ?? null;
}
